import type { TableHeaderCellArrow } from "@sumprio/ui";

export type DataTableSortDirection = "asc" | "desc";

export type DataTableGetSortValue<T> = (_item: T, _columnId: string) => string | number;

export function defaultDataTableGetSortValue<T>(item: T, columnId: string): string | number {
  const record = item as Record<string, unknown>;
  const value = record[columnId];
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "object") {
    const nested =
      (value as Record<string, unknown>).name ?? (value as Record<string, unknown>).label;
    if (nested !== null && nested !== undefined) {
      return String(nested).toLowerCase();
    }
    return JSON.stringify(value).toLowerCase();
  }
  return String(value).toLowerCase();
}

export function sortItemsByColumn<T>(
  items: readonly T[],
  sortColumnId: string | null | undefined,
  direction: DataTableSortDirection | undefined,
  getSortValue: DataTableGetSortValue<T> = defaultDataTableGetSortValue
): T[] {
  if (!sortColumnId || !direction) {
    return [...items];
  }
  const factor = direction === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const left = getSortValue(a, sortColumnId);
    const right = getSortValue(b, sortColumnId);
    if (typeof left === "number" && typeof right === "number") {
      return (left - right) * factor;
    }
    return String(left).localeCompare(String(right), undefined, { sensitivity: "base" }) * factor;
  });
}

export function getDataTableSortArrow(
  sortColumnId: string | null | undefined,
  columnId: string,
  direction: DataTableSortDirection | undefined
): TableHeaderCellArrow {
  if (!sortColumnId || sortColumnId !== columnId || !direction) {
    return false;
  }
  return direction === "asc" ? "up" : "down";
}

export function getNextDataTableSort(
  currentColumnId: string | null | undefined,
  nextColumnId: string,
  currentDirection: DataTableSortDirection
): { columnId: string; direction: DataTableSortDirection } {
  if (currentColumnId === nextColumnId) {
    return {
      columnId: nextColumnId,
      direction: currentDirection === "asc" ? "desc" : "asc",
    };
  }
  return { columnId: nextColumnId, direction: "asc" };
}
